'use client';

import {
  DefaultError,
  QueryKey,
  UndefinedInitialDataOptions,
  useQuery,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React from 'react';
import { toast } from 'sonner';

class CustomAxiosError extends AxiosError<{
  code?: string;
  message: string;
}> {}

type UseQueryOptions<
  TQueryFnData = unknown,
  TError = DefaultError & CustomAxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>;

export type UseApiQueryOptions<
  TQueryFnData = unknown,
  TError = DefaultError & CustomAxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>;

export const useApiQuery = <
  TQueryFnData = unknown,
  TError = DefaultError & CustomAxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  const res = useQuery(options);

  const handleJWTExpiration = React.useCallback((error: TError) => {
    const axiosError = error as CustomAxiosError;

    if (axiosError?.response?.status?.toString().startsWith('5')) {
      toast.error('Something went wrong. Please try again later.');
    }
    if (axiosError?.response?.status?.toString().startsWith('4')) {
      toast.error(
        axiosError?.response?.data?.message ||
          'Something went wrong. Please try again later.',
      );
    }
  }, []);

  React.useEffect(() => {
    if (res.error) {
      handleJWTExpiration(res.error);
    }
  }, [res.error, handleJWTExpiration]);

  return res;
};
